import React from 'react';
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';
import { HERO_ACCENT, type HeroAspect } from '../schema';
import { HERO_FONTS } from '../fonts';

const WORD_STAGGER_FRAMES = 3;
const EXIT_FRAMES = 8;

const normalize = (w: string) => w.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * A belief phrase in Oswald uppercase: words spring in with a stagger, the accent word carries the blue, the whole
 * line holds and then lifts out. Only ever one on screen (enforced by validateHeroProps).
 */
export const KineticPhrase: React.FC<{
  text: string;
  accentWord?: string;
  durationInFrames: number;
  aspect: HeroAspect;
}> = ({ text, accentWord, durationInFrames, aspect }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const words = text.trim().split(/\s+/);
  const accent = accentWord ? normalize(accentWord) : '';
  const vertical = aspect === '9:16';
  const fontSize = vertical ? 118 : 108;

  const exitStart = Math.max(0, durationInFrames - EXIT_FRAMES);
  const exitOpacity = interpolate(frame, [exitStart, durationInFrames], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const exitY = interpolate(frame, [exitStart, durationInFrames], [0, -18], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const vignette = interpolate(frame, [0, 8], [0, 1], { extrapolateRight: 'clamp' }) * exitOpacity;

  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <AbsoluteFill
        style={{
          opacity: vignette * 0.85,
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0) 70%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: '7%',
          right: '7%',
          top: vertical ? '42%' : '50%',
          transform: `translateY(-50%) translateY(${exitY}px)`,
          opacity: exitOpacity,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'baseline',
          columnGap: '0.26em',
          rowGap: '0.02em',
          fontFamily: HERO_FONTS.display,
          fontWeight: 700,
          fontSize,
          lineHeight: 1.02,
          letterSpacing: '0.012em',
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        {words.map((word, i) => {
          const wordFrame = frame - i * WORD_STAGGER_FRAMES;
          const s = spring({ frame: wordFrame, fps, config: { damping: 14, stiffness: 190, mass: 0.7 } });
          const opacity = interpolate(wordFrame, [0, 6], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
          const y = interpolate(s, [0, 1], [46, 0]);
          const scale = interpolate(s, [0, 1], [1.18, 1]);
          const isAccent = accent !== '' && normalize(word) === accent;
          return (
            <span
              key={`${word}-${i}`}
              style={{
                display: 'inline-block',
                opacity,
                transform: `translateY(${y}px) scale(${scale})`,
                color: isAccent ? HERO_ACCENT : '#FFFFFF',
                textShadow: isAccent
                  ? `0 0 28px rgba(2,116,190,0.75), 0 6px 30px rgba(0,0,0,0.7)`
                  : '0 6px 30px rgba(0,0,0,0.7), 0 2px 6px rgba(0,0,0,0.6)',
              }}
            >
              {word}
            </span>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
